import {
  CcfAgentLegConnectedIcon,
  CcfAgentLegDialingIcon,
  CcfAgentLegDisconnectedIcon,
  CcfBox,
  CcfLoader,
  CcfTooltip,
  useTranslator,
} from '@nice-ccf/ui-controls';
import agentLegStyle from './ccf-agent-leg.styles';
import { Box, IconButton, useTheme } from '@mui/material';
import { useEffect, useState } from 'react';
import { AgentLegStatus } from '@nice-ccf/common-sdk';
import ccfAppHeaderStyles from '../ccf-app-header/ccf-app-header-styles';
import { useSelector } from 'react-redux';
import { activeContactSelector } from '../ccf-inbox/ccf-inbox.slice';
import { DigitalChannelStatus, VoiceContactStatus } from '@nice-ccf/core-sdk';
import styles from './ccf-agent-leg.module.scss';

/* eslint-disable-next-line */
export interface CcfAgentLegProps {}

/**
 * Component displays an icon with the different agent leg status
 * @returns tooltip with different agent leg status
 * @example <CcfAgentLeg/>
 */
export function CcfAgentLeg(props: CcfAgentLegProps) {
  const [translate] = useTranslator();
  const theme = useTheme();
  const agentLegStyles = agentLegStyle(theme);
  const appHeaderStyles = ccfAppHeaderStyles(theme);
  const activeContact = useSelector(activeContactSelector);
  const [agentLegConnectedStatus, setAgentLegConnectedStatus] = useState(
    AgentLegStatus.DISCONNECTED
  );

  const agentLegIconMap = new Map([
    [
      '' || AgentLegStatus.DISCONNECTED.toString(),
      <CcfAgentLegDisconnectedIcon
        data-testid="disconnected"
        fill="#ffffff"
        viewBox="0 -2 32 24"
      />,
    ],
    [
      AgentLegStatus.DIALING.toString(),
      <>
        <CcfAgentLegDialingIcon
          fill="#ffffff"
          style={{ marginBottom: '-8px', marginLeft: '7px' }}
        />
        <CcfLoader
          showLoadingText={false}
          isPrimary={false}
          brandingColor="#ffffff"
        />
      </>,
    ],
    [
      AgentLegStatus.ACTIVE.toString(),
      <CcfAgentLegConnectedIcon fill="#ffffff" viewBox="-4 -5 32 24" />,
    ],
  ]);

  /**
   * Function to handle different agent leg like - connect or disconnect
   * @param e - React.SyntheticEvent
   * @example updateAgentLeg(e)
   */
  const updateAgentLeg = (e: React.SyntheticEvent) => {
    setAgentLegConnectedStatus(AgentLegStatus.DIALING);
    e.preventDefault();
    setTimeout(() => {
      if (agentLegConnectedStatus === AgentLegStatus.ACTIVE) {
        setAgentLegConnectedStatus(AgentLegStatus.DISCONNECTED);
      } else if (agentLegConnectedStatus === AgentLegStatus.DISCONNECTED) {
        setAgentLegConnectedStatus(AgentLegStatus.ACTIVE);
      }
    }, 3000);
  };

  useEffect(() => {
    if (
      activeContact?.contactStatus === DigitalChannelStatus.OPEN ||
      activeContact?.contactStatus === VoiceContactStatus.ACTIVE
    ) {
      if (agentLegConnectedStatus === AgentLegStatus.DISCONNECTED) {
        setAgentLegConnectedStatus(AgentLegStatus.DIALING);
        setTimeout(() => {
          setAgentLegConnectedStatus(AgentLegStatus.ACTIVE);
        }, 3000);
      }
    }
  }, [activeContact?.contactStatus]);

  /**
   * Function to give translated text of agentLegStatus
   * @param agentLegStatus - agentLegStatusValue
   * @example getAgentLegStatusTranslated('Active')
   */
  const getAgentLegStatusTranslated = (agentLegStatus: string) => {
    switch (agentLegStatus) {
      case AgentLegStatus.DIALING:
        return translate('agentLegDialing');
      case AgentLegStatus.ACTIVE:
        return (
          <>
            <div>{translate('agentLegConnected')}</div>
            <div>{translate('clickToDisconnect')}</div>
          </>
        );
      default:
        return (
          <>
            <div>{translate('agentLegDisconnected')}</div>
            <div>{translate('clickToConnect')}</div>
          </>
        );
    }
  };

  return (
    <CcfBox>
      <CcfTooltip
        title={
          <Box>{getAgentLegStatusTranslated(agentLegConnectedStatus)}</Box>
        }
        arrow
        tooltipForRTL="ccfTooltipRight0"
        aria-label={translate('agentLeg')}
      >
        <IconButton
          color="inherit"
          sx={[appHeaderStyles.button, agentLegStyles.agentLeg]}
          disableFocusRipple={true}
          role="button"
          onClick={updateAgentLeg}
          data-testid="agent-leg-status"
          className={styles.agentLeg}
        >
          {agentLegIconMap.get(agentLegConnectedStatus)}
        </IconButton>
      </CcfTooltip>
    </CcfBox>
  );
}

export default CcfAgentLeg;
